import { getMarkRange, Mark, mergeAttributes } from "@tiptap/core"
import { Plugin, PluginKey, TextSelection } from "@tiptap/pm/state"

import type { BadgeAttributes, BadgeColor } from "./badge-html"
import {
	badgeElementAttributes,
	DEFAULT_BADGE_COLOR,
	normalizeBadgeAttributes,
} from "./badge-html"

declare module "@tiptap/core" {
	interface Commands<ReturnType> {
		badge: {
			setBadge: (attributes?: Partial<BadgeAttributes>) => ReturnType
			toggleBadge: (attributes?: Partial<BadgeAttributes>) => ReturnType
			unsetBadge: () => ReturnType
			/** Changes the badge at the caret, or every badge in the selection. */
			updateBadge: (attributes: Partial<BadgeAttributes>) => ReturnType
			setBadgeColor: (color: BadgeColor) => ReturnType
			insertBadge: (
				text?: string,
				attributes?: Partial<BadgeAttributes>,
			) => ReturnType
		}
	}
}

const attribute = (name: keyof BadgeAttributes, fallback: unknown) => ({
	default: fallback,
	parseHTML: (element: HTMLElement) =>
		normalizeBadgeAttributes({ [name]: element.getAttribute(`data-${name}`) })[
			name
		],
	rendered: false,
})

export const BadgeMark = Mark.create({
	name: "badge",
	// Above Link's 1000, so one badge wraps a bold or linked run as a single
	// element instead of breaking into one pill per run.
	priority: 1001,
	inclusive: true,
	spanning: true,

	addAttributes() {
		return {
			color: attribute("color", DEFAULT_BADGE_COLOR),
			size: attribute("size", "md"),
			shape: attribute("shape", "rounded"),
			icon: attribute("icon", null),
			stroke: attribute("stroke", false),
			disabled: attribute("disabled", false),
		}
	},

	parseHTML() {
		return [{ tag: 'span[data-type="badge"]' }]
	},

	renderHTML({ mark, HTMLAttributes }) {
		const attributes = normalizeBadgeAttributes(mark.attrs)
		return [
			"span",
			mergeAttributes(HTMLAttributes, badgeElementAttributes(attributes)),
			0,
		]
	},

	addKeyboardShortcuts() {
		return {
			// End lands before the trailing space, which is still inside the pill.
			End: ({ editor }) => {
				const { $from, empty } = editor.state.selection
				if (!empty || !this.type.isInSet($from.nodeBefore?.marks ?? [])) {
					return false
				}
				const rest = editor.state.doc.textBetween($from.pos, $from.end())
				if (rest !== " ") return false
				return editor.commands.setTextSelection($from.end())
			},
		}
	},

	addProseMirrorPlugins() {
		const type = this.type
		// True once the caret was placed (click, End, arrows) rather than moved by typing.
		const key = new PluginKey<boolean>("badgeEscape")
		return [
			new Plugin<boolean>({
				key,
				state: {
					init: () => false,
					apply: (tr, placed) =>
						tr.docChanged ? false : tr.selectionSet ? true : placed,
				},
				props: {
					// Chrome keeps the caret before the pill's trailing space at the end of a
					// line, so a click or End past a badge lands inside it. Text typed there
					// goes after the badge instead; typing straight on keeps extending it.
					handleTextInput(view, from, to, text) {
						if (from !== to || !key.getState(view.state)) return false
						const $pos = view.state.doc.resolve(from)
						if (!type.isInSet($pos.nodeBefore?.marks ?? [])) return false
						if (type.isInSet($pos.nodeAfter?.marks ?? [])) return false
						const at = $pos.nodeAfter?.text?.startsWith(" ") ? from + 1 : from
						const tr = view.state.tr.insertText(text, at, at)
						tr.removeMark(at, at + text.length, type)
						tr.setSelection(TextSelection.create(tr.doc, at + text.length))
						view.dispatch(tr)
						return true
					},
				},
			}),
		]
	},

	addCommands() {
		return {
			setBadge:
				(attributes = {}) =>
				({ commands }) =>
					commands.setMark(this.name, normalizeBadgeAttributes(attributes)),

			toggleBadge:
				(attributes = {}) =>
				({ commands }) =>
					commands.toggleMark(this.name, normalizeBadgeAttributes(attributes)),

			unsetBadge:
				() =>
				({ commands }) =>
					commands.unsetMark(this.name, { extendEmptyMarkRange: true }),

			updateBadge:
				(changes) =>
				({ state, tr, dispatch }) => {
					const type = state.schema.marks[this.name]
					if (!type) return false
					const { from, to, empty, $from } = state.selection
					const ranges: { from: number; to: number }[] = []
					if (empty) {
						const range = getMarkRange($from, type)
						if (range) ranges.push(range)
					} else {
						ranges.push({ from, to })
					}
					let changed = false
					for (const range of ranges) {
						state.doc.nodesBetween(range.from, range.to, (node, pos) => {
							if (!node.isText) return true
							const mark = type.isInSet(node.marks)
							if (!mark) return false
							const start = Math.max(pos, range.from)
							const end = Math.min(pos + node.nodeSize, range.to)
							const next = type.create(
								normalizeBadgeAttributes({ ...mark.attrs, ...changes }),
							)
							if (dispatch) {
								tr.removeMark(start, end, type)
								tr.addMark(start, end, next)
							}
							changed = true
							return false
						})
					}
					return changed
				},

			setBadgeColor:
				(color) =>
				({ commands }) =>
					commands.updateBadge({ color }),

			insertBadge:
				(text = "Badge", attributes = {}) =>
				({ state, tr, dispatch }) => {
					const type = state.schema.marks[this.name]
					if (!type || !text) return false
					if (dispatch) {
						const from = state.selection.from
						const mark = type.create(normalizeBadgeAttributes(attributes))
						tr.replaceSelectionWith(state.schema.text(text, [mark]), false)
						// A plain space after the pill gives the caret somewhere to type
						// outside it, since the mark extends at its end.
						tr.insertText(" ", from + text.length)
						tr.removeMark(from + text.length, from + text.length + 1, type)
						tr.setSelection(
							TextSelection.create(tr.doc, from, from + text.length),
						)
					}
					return true
				},
		}
	},
})

export const BadgeExtensions = [BadgeMark]
